import { css } from 'astroturf';
import fetchJsonp from 'fetch-jsonp';
import React from 'react';

import Column from './Column';
import Checkout from '../checkout';
import { parseAmount, formatMoney } from '../../utils/money';

const styles = css`
  .currency {
    margin: 1rem auto -2.5rem;
    max-width: 75rem;
    display: flex;
    flex-flow: row wrap;
    justify-content: center;
    align-items: stretch;
    padding-left: env(safe-area-inset-left, 0);
    padding-right: env(safe-area-inset-right, 0);
    color: #007ab3;
    font-size: 0.8125rem;

    @media (min-width: 1201px) {
      margin: 0rem auto -1.5rem;
      justify-content: flex-end;
    }
  }
  .flexwrap {
    margin: 2rem auto 1rem;
    max-width: 75rem;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    align-items: stretch;
    padding-left: env(safe-area-inset-left, 0);
    padding-right: env(safe-area-inset-right, 0);

    @media (min-width: 1201px) {
      position: relative;
      flex-direction: row;
      justify-content: space-between;
      gap: 1rem;
    }
  }
`;

const currencies = {
  USD: 'US Dollar',
  EUR: 'Euro',
  GBP: 'Pound Sterling',
  ARS: 'Argentine Peso',
  AUD: 'Australian Dollar',
  CAD: 'Canadian Dollar',
  CHF: 'Swiss Franc',
  CZK: 'Czech Koruna',
  DKK: 'Danish Krone',
  HKD: 'Hong Kong Dollar',
  HUF: 'Hungarian Forint',
  INR: 'Indian Rupee',
  ILS: 'Israeli Shekel',
  JPY: 'Japanese Yen',
  KRW: 'South Korean Won',
  MXN: 'Mexican Peso',
  NOK: 'Norwegian Krone',
  NZD: 'New Zealand Dollar',
  PLN: 'Polish Zloty',
  RUB: 'Russian Ruble',
  SEK: 'Swedish Krona',
  SGD: 'Singapore Dollar',
  THB: 'Thai Baht',
  TRY: 'Turkish Lira',
  TWD: 'New Taiwan Dollar',
  UAH: 'Ukrainian Hryvnia',
};

// `state.currency` throughout this component is the display name ("Euro"), not an ISO
// code — this is the reverse lookup back to a real code ("EUR") for Intl.NumberFormat.
const currencyCodes = Object.fromEntries(Object.entries(currencies).map(([code, name]) => [name, code]));

const PRICING_IDS = {
  proYearly: 599947,
  proMonthly: 599945,
  teamYearly: 929657,
  teamMonthly: 929656,
  teamYearlyOld: 599949,
  teamMonthlyOld: 599948,
  proYearlyOld: 565952,
};

const MIN_SEATS = 2;
const MAX_SEATS = 20;

const pricing = {
  yearly: {
    pro: {
      price: '  ',
      id: PRICING_IDS.proYearly,
      monthId: 566022,
    },
    team: {
      price: '   ',
      id: PRICING_IDS.teamYearly,
      monthId: 929659,
    },
    oldPro: {
      id: PRICING_IDS.proYearlyOld,
    },
  },
  monthly: {
    pro: {
      price: '   ',
      id: PRICING_IDS.proMonthly,
    },
    team: {
      price: '   ',
      id: PRICING_IDS.teamMonthly,
    },
  },
};

const getCoupon = () => {
  if (typeof window !== 'undefined') {
    const params = new URL(document.location).searchParams;
    if (params.get('coupon')) {
      const coupon = params.get('coupon');
      sessionStorage.coupon = coupon;
    }
    return sessionStorage.coupon;
  }

  return false;
};

const getCurrency = () =>
  fetchJsonp(`https://checkout.paddle.com/api/2.0/prices?product_ids=${PRICING_IDS.proYearly}`)
    .then((response) => response.json())
    .then((json) => currencies[json.response.products[0].currency])
    .catch(() => false);

const emailQuote = `<span style="display:block;width: 300px;padding:0.5rem 0;text-wrap: pretty">"One of the best SaaS email support out there. If you reply with a kudos, question,
                    or any other random thought, you'll hear from Kilian. If they say they're going to
                    get back to you, they get back to you." - <i>Drew, Creative Director</i></span>`;

class Table extends React.Component {
  constructor() {
    super();

    this.state = {
      country: '',
      oldProYearly: '',
      proYearly: '',
      currency: false,
      proYearlyHasVAT: false,
      proYearlyVAT: '',
      proYearlyPerMonth: pricing.yearly.pro.price,
      proMonthly: pricing.monthly.pro.price,
      proMonthlyHasVAT: false,
      proMonthlyVAT: '',
      // per-seat unit prices (raw numbers, parsed back out of Paddle's pre-formatted
      // strings) — the displayed team price is these multiplied by `seats` at render
      // time, so changing the seat count never needs a Paddle refetch.
      teamYearlyUnitNet: NaN,
      teamYearlyUnitGross: NaN,
      teamYearlyUnitTax: NaN,
      teamYearlyPerMonthUnit: NaN,
      teamMonthlyUnitNet: NaN,
      teamMonthlyUnitGross: NaN,
      teamMonthlyUnitTax: NaN,
      seats: MIN_SEATS,
      pricingPopup: {
        id: '',
        visible: false,
        name: 'Polypane',
        priceYearly: false,
        total: 0,
        net: 0,
        checkoutprice: 0,
        priceInfo: {},
        sub: 'pro',
      },
      coupon: getCoupon(),
      highLightedPlan: false,
      expandedFeatures: false,
    };
  }

  componentDidMount() {
    getCurrency().then((currency) => {
      this.setState({ currency });
    });

    const params = new URL(document.location).searchParams;
    if (params.get('plan')) {
      this.setState({ highLightedPlan: params.get('plan') });
    }

    const conditionallySetupPaddle = () => {
      if (window.Paddle) {
        this.setupPaddle();
      } else {
        setTimeout(() => conditionallySetupPaddle(), 100);
      }
    };

    conditionallySetupPaddle();
  }

  setupPaddle = () => {
    const { Paddle } = window;
    const ref = this;

    Paddle.Setup({
      vendor: 39438,
      debug: false,
      eventCallback(data) {
        if (
          [
            'Checkout.Loaded',
            'Checkout.Coupon.Applied',
            'Checkout.Coupon.Remove',
            'Checkout.Vat.Applied',
            'Checkout.Vat.Remove',
          ].includes(data.event)
        ) {
          const { id } = data.eventData.product;
          const { currency } = data.eventData.checkout.recurring_prices.customer;

          let currentprice = new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency,
            currencyDisplay: 'symbol',
          }).format(data.eventData.checkout.prices.customer.total);

          if (currentprice.endsWith('.00')) {
            [currentprice] = currentprice.split('.00');
          }

          if (currentprice.startsWith('US$')) {
            currentprice = currentprice.replace('US$', '$');
          }

          let price = new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency,
            currencyDisplay: 'symbol',
          }).format(data.eventData.checkout.recurring_prices.customer.total);

          if (price.endsWith('00')) {
            [price] = price.split('.00');
          }

          if (price.startsWith('US$')) {
            price = price.replace('US$', '$');
          }

          let net = new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency,
            currencyDisplay: 'symbol',
          }).format(data.eventData.checkout.recurring_prices.customer.items[0].display_price.line_price.net);

          if (net.endsWith('00')) {
            [net] = price.split('.00');
          }

          if (net.startsWith('US$')) {
            net = net.replace('US$', '$');
          }

          const hasVat = data.eventData.checkout.recurring_prices.customer.total_tax != '0.00';
          let vat = new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency,
            currencyDisplay: 'symbol',
          }).format(data.eventData.checkout.recurring_prices.customer.total_tax);

          if (vat.endsWith('00')) {
            [vat] = vat.split('.00');
          }

          const taxrate = data.eventData.checkout.recurring_prices.customer.items[0].tax_rate * 100;

          let discount = new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency,
            currencyDisplay: 'symbol',
          }).format(data.eventData.checkout.recurring_prices.customer.items[0].line_price.net_discount * -1);

          if (discount.endsWith('00')) {
            [discount] = discount.split('.00');
          }

          const currentState = ref.state.pricingPopup;
          currentState.id = id;
          currentState.checkoutprice = currentprice;
          currentState.total = price;

          currentState.priceInfo = {
            discount,
            taxrate,
            id,
            price,
            net,
            vat,
            hasVat,
          };
          ref.setState({ pricingPopup: currentState, coupon: data.eventData.checkout.coupon.coupon_code });
        }
      },
    });

    Object.entries(pricing.yearly).forEach((item) => {
      if (item[0] === 'pro') {
        Paddle.Product.Prices(item[1].id, (prices) => {
          let price = prices.recurring.price.gross;

          if (price.endsWith('00')) {
            [price] = price.split('.00');
          }
          if (price.startsWith('US$')) {
            price = price.replace('US$', '$');
          }

          this.setState({
            country: prices.country,
            proYearly: price,
            proYearlyNet: prices.recurring.price.net,
            proYearlyVAT: prices.recurring.price.tax,
            proYearlyHasVAT: prices.recurring.price.net !== prices.recurring.price.gross,
          });
        });

        Paddle.Product.Prices(item[1].monthId, (prices) => {
          let price = prices.recurring.price.gross;

          if (price.endsWith('00')) {
            [price] = price.split('.00');
          }
          if (price.startsWith('US$')) {
            price = price.replace('US$', '$');
          }
          this.setState({ proYearlyPerMonth: price });
        });
      } else if (item[0] === 'team') {
        // Stored as raw per-seat numbers, not pre-formatted strings — the displayed
        // total is these multiplied by the selected seat count, computed in render().
        Paddle.Product.Prices(item[1].id, (prices) => {
          this.setState({
            teamYearlyUnitNet: parseAmount(prices.recurring.price.net),
            teamYearlyUnitGross: parseAmount(prices.recurring.price.gross),
            teamYearlyUnitTax: parseAmount(prices.recurring.price.tax),
          });
        });
        Paddle.Product.Prices(item[1].monthId, (prices) => {
          this.setState({ teamYearlyPerMonthUnit: parseAmount(prices.recurring.price.gross) });
        });
      } else if (item[0] === 'oldPro') {
        Paddle.Product.Prices(item[1].id, (prices) => {
          let price = prices.recurring.price.net;

          if (price.endsWith('00')) {
            [price] = price.split('.00');
          }
          if (price.startsWith('US$')) {
            price = price.replace('US$', '$');
          }
          this.setState({
            oldProYearly: price,
          });
        });
      }
    });
    Object.entries(pricing.monthly).forEach((item) => {
      if (item[0] === 'pro') {
        Paddle.Product.Prices(item[1].id, (prices) => {
          let price = prices.recurring.price.gross;

          if (price.endsWith('00')) {
            [price] = price.split('.00');
          }
          if (price.startsWith('US$')) {
            price = price.replace('US$', '$');
          }
          this.setState({
            proMonthly: price,
            proMonthlyNet: prices.recurring.price.net,
            proMonthlyGross: prices.recurring.price.gross,
            proMonthlyVAT: prices.recurring.price.tax,
            proMonthlyHasVAT: prices.recurring.price.net !== prices.recurring.price.gross,
          });
        });
      } else if (item[0] === 'team') {
        Paddle.Product.Prices(item[1].id, (prices) => {
          this.setState({
            teamMonthlyUnitNet: parseAmount(prices.recurring.price.net),
            teamMonthlyUnitGross: parseAmount(prices.recurring.price.gross),
            teamMonthlyUnitTax: parseAmount(prices.recurring.price.tax),
          });
        });
      }
    });
  };

  updateSeats = (seats) => {
    this.setState({ seats: Math.min(Math.max(seats, MIN_SEATS), MAX_SEATS) });
  };

  setSignUpVisible = (pricingPopup) => {
    const ref = this;
    ref.setState({ pricingPopup });
  };

  setExpandedFeatures = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState((prevState) => ({
      expandedFeatures: !prevState.expandedFeatures,
    }));
    return false;
  };

  render() {
    const { state, props } = this;
    const { pricingPopup, coupon, currency, seats } = state;
    const currencyCode = currencyCodes[currency];

    // "Billed yearly"/checkout totals — these must reflect the actual amount charged
    // for the selected seat count.
    const teamYearlyTotal = formatMoney(state.teamYearlyUnitGross * seats, currencyCode) || '   ';
    const teamYearlyNetTotal = formatMoney(state.teamYearlyUnitNet * seats, currencyCode) || '';
    const teamYearlyVATTotal = formatMoney(state.teamYearlyUnitTax * seats, currencyCode) || '';
    const teamMonthlyTotal = formatMoney(state.teamMonthlyUnitGross * seats, currencyCode) || '   ';
    const teamMonthlyNetTotal = formatMoney(state.teamMonthlyUnitNet * seats, currencyCode) || '';
    const teamMonthlyVATTotal = formatMoney(state.teamMonthlyUnitTax * seats, currencyCode) || '';

    // The headline price is the flat per-seat rate, not multiplied by the seat count —
    // "€12/seat/month", not the total for whatever the stepper happens to be set to.
    const teamYearlyPerMonthUnitFormatted = formatMoney(state.teamYearlyPerMonthUnit, currencyCode) || '   ';
    const teamMonthlyUnitFormatted = formatMoney(state.teamMonthlyUnitGross, currencyCode) || '   ';

    // Yearly-billing savings scale with the selected seat count, unlike Individual's
    // flat number — this is (monthly rate − yearly-equivalent rate) × 12 × seats.
    const teamYearlySavings = Math.round(
      (state.teamMonthlyUnitGross - state.teamYearlyPerMonthUnit) * 12 * seats
    );

    return (
      <>
        <div className={styles.currency}>
          {currency ? (
            <span>
              prices shown in <strong>{currency}</strong>
            </span>
          ) : (
            <>&nbsp;</>
          )}
        </div>
        <div className={styles.flexwrap}>
          <Column
            freeTrial={true && !state.highLightedPlan}
            priceYearly={props.priceYearly}
            name="Free trial"
            highlighted={false}
            priceInfo={[
              {
                price: ' ',
                id: pricing.yearly.pro.id,
                vat: ' ',
                hasVat: false,
              },
              {
                price: ' ',
                id: pricing.monthly.pro.id,
                vat: ' ',
                hasVat: false,
              },
            ]}
            localPrice={' '}
            desc="Use for free for 14 days"
            smalldesc=""
            benefits={[
              'Full access to every feature',
              '<b>No credit card needed</b>',
              '<b>Onboarding chat support</b>',
              'Decide at the end of your trial',
            ]}
            buttonText="Start free trial"
            setSignUpVisible={this.setSignUpVisible}
            togglePricing={props.togglePricing}
            currency={currency}
          />

          <Column
            priceYearly={props.priceYearly}
            name="Individual"
            highlighted={state.highLightedPlan === 'Individual'}
            priceInfo={[
              {
                price: state.proYearlyPerMonth,
                net: state.proYearlyNet,
                id: pricing.yearly.pro.id,
                vat: state.proYearlyVAT,
                hasVat: state.proYearlyHasVAT,
              },
              {
                price: state.proMonthly,
                net: state.proMonthlyNet,
                id: pricing.monthly.pro.id,
                vat: state.proMonthlyVAT,
                hasVat: state.proMonthlyHasVAT,
              },
            ]}
            localPrice={props.priceYearly ? state.proYearly : state.proMonthly}
            oldLocalPrice={state.oldProYearly}
            desc="Perfect for freelancers, solo devs or personal projects."
            smalldesc=""
            benefits={[
              'For one person',
              'Personal and commercial use',
              'Use on <strong>3 personal devices</strong>',
              '<strong>Human</strong> email support',
            ]}
            tooltips={[
              false,
              false,
              `No need to switch licences between computers, run Polypane on 3 computers simultaneously.`,
              emailQuote,
            ]}
            buttonText={`Subscribe ${props.priceYearly ? 'yearly' : 'monthly'}`}
            setSignUpVisible={this.setSignUpVisible}
            togglePricing={props.togglePricing}
            currency={currency}
            expandedFeatures={state.expandedFeatures}
            setExpandedFeatures={this.setExpandedFeatures}
          />

          <Column
            priceYearly={props.priceYearly}
            name="Business"
            highlighted={state.highLightedPlan === 'Business'}
            priceInfo={[
              {
                price: teamYearlyPerMonthUnitFormatted,
                net: teamYearlyNetTotal,
                id: pricing.yearly.team.id,
                vat: teamYearlyVATTotal,
                hasVat: state.teamYearlyUnitNet !== state.teamYearlyUnitGross,
              },
              {
                price: teamMonthlyUnitFormatted,
                net: teamMonthlyNetTotal,
                id: pricing.monthly.team.id,
                vat: teamMonthlyVATTotal,
                hasVat: state.teamMonthlyUnitNet !== state.teamMonthlyUnitGross,
              },
            ]}
            localPrice={props.priceYearly ? teamYearlyTotal : teamMonthlyTotal}
            yearlySavings={teamYearlySavings}
            desc="One subscription to cover your entire team."
            benefits={[
              'From <strong>2 to 20</strong> seats',
              'Use on <strong>3 computers</strong> per user',
              'Add or remove seats <strong>as needed</strong>',
              'Easily re-assign licenses',
              'Centralized billing',
              'Priority feature requests',
              '<b>Human</b> email &amp; chat support',
            ]}
            tooltips={[false, false, false, false, false, false, emailQuote]}
            buttonText={`Subscribe ${props.priceYearly ? 'yearly' : 'monthly'}`}
            setSignUpVisible={this.setSignUpVisible}
            togglePricing={props.togglePricing}
            currency={currency}
            expandedFeatures={state.expandedFeatures}
            setExpandedFeatures={this.setExpandedFeatures}
            seats={seats}
            minSeats={MIN_SEATS}
            maxSeats={MAX_SEATS}
            onSeatsChange={this.updateSeats}
            unitNet={props.priceYearly ? state.teamYearlyUnitNet : state.teamMonthlyUnitNet}
            unitVat={props.priceYearly ? state.teamYearlyUnitTax : state.teamMonthlyUnitTax}
            unitGross={props.priceYearly ? state.teamYearlyUnitGross : state.teamMonthlyUnitGross}
            currencyCode={currencyCode}
          />

          <Column
            name="Enterprise"
            highlighted={state.highLightedPlan === 'Enterprise'}
            priceInfo={[
              {
                price: 'Custom',
              },
              {
                price: 'Custom',
              },
            ]}
            localPrice=" "
            desc="For large companies with multiple&nbsp;teams."
            benefits={[
              'Typically <strong>20 or more</strong> seats',
              'Use on <strong>3 computers</strong> per user',
              'Advanced user management',
              '<b>Custom</b> billing options',
              'Priority feature requests',
              '<b>Human</b> email &amp; chat support',
              'Single sign-on &amp; SCIM',
              'Training & onboarding',
              'Supplier portal registration',
            ]}
            tooltips={[false, false, false, false, false, emailQuote, false, false]}
            buttonText="Contact us"
            togglePricing={props.togglePricing}
            currency={currency}
            expandedFeatures={state.expandedFeatures}
            setExpandedFeatures={this.setExpandedFeatures}
          />

          {/* <SignUp
            visible={pricingPopup.visible}
            setSignUpVisible={this.setSignUpVisible}
            id={pricingPopup.id}
            name={pricingPopup.name}
            priceYearly={pricingPopup.priceYearly}
            total={pricingPopup.total}
            checkoutprice={pricingPopup.checkoutprice}
            priceInfo={pricingPopup.priceInfo}
            coupon={coupon}
            togglePricing={this.togglePricing}
            pricingPopup={pricingPopup}
            country={this.state.country}
          /> */}

          <Checkout
            currency={currency}
            visible={pricingPopup.visible}
            setSignUpVisible={this.setSignUpVisible}
            id={pricingPopup.id}
            name={pricingPopup.name}
            priceYearly={pricingPopup.priceYearly}
            total={pricingPopup.total}
            checkoutprice={pricingPopup.checkoutprice}
            priceInfo={pricingPopup.priceInfo}
            coupon={coupon}
            togglePricing={this.togglePricing}
            pricingPopup={pricingPopup}
            country={this.state.country}
            minSeats={pricingPopup.minSeats}
            maxSeats={pricingPopup.maxSeats}
            initialSeats={pricingPopup.initialSeats}
            unitNet={pricingPopup.unitNet}
            unitVat={pricingPopup.unitVat}
            unitGross={pricingPopup.unitGross}
            currencyCode={pricingPopup.currencyCode}
          />
        </div>
      </>
    );
  }
}
export default Table;
